import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { autorisationsAbsenceAPI, joursFeriesAPI } from '../../../services/api';
import { AutorisationAbsence, AutorisationAbsenceCreate, AutorisationAbsenceUpdate } from '../../../types/autorisationAbsence';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import {
  SlideUpTransition,
  useMinimizable,
  DialogControls,
  MinimizedBar,
} from '../../../components/MinimizableDialog';

const HR_COLOR = '#00897B';

interface AutorisationAbsenceFormDialogProps {
  open: boolean;
  onClose: () => void;
  autorisation: AutorisationAbsence | null;
  employees: EmployeeDropdownItem[];
  onSuccess: () => void;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const AutorisationAbsenceFormDialog: React.FC<AutorisationAbsenceFormDialogProps> = ({
  open,
  onClose,
  autorisation,
  employees,
  onSuccess,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDropdownItem | null>(null);
  const [formData, setFormData] = useState<{
    date_debut: string;
    date_fin: string;
    motif: string;
  }>({
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: new Date().toISOString().split('T')[0],
    motif: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [nbrJours, setNbrJours] = useState<number>(1);
  const [feries, setFeries] = useState<Set<string>>(new Set());

  const { showSuccess, showError } = useAlert();

  // Draft caching — only for new autorisations (never when editing an existing
  // row). Derived state (nbrJours / feries) is recomputed by the effects from
  // the cached dates, so it is intentionally not stored.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    selectedEmployee: EmployeeDropdownItem | null;
    formData: typeof formData;
  }>('autorisation-absence', !autorisation);

  // Load public holidays (jours fériés) so the day count can skip them
  useEffect(() => {
    if (!open) return;
    let active = true;
    joursFeriesAPI
      .getDates()
      .then((dates) => {
        if (active) setFeries(new Set(dates.map((d) => d.slice(0, 10))));
      })
      .catch(() => {
        if (active) setFeries(new Set());
      });
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      if (autorisation) {
        // Find the employee in the list
        const emp = employees.find(e => e.id === autorisation.employee_id) || null;
        setSelectedEmployee(emp);
        setFormData({
          date_debut: autorisation.date_debut,
          date_fin: autorisation.date_fin,
          motif: autorisation.motif || '',
        });
        setNbrJours(autorisation.nbr_jours);
      } else {
        // New mode — repopulate from a draft if one was stashed by an accidental
        // backdrop/Escape close, otherwise do the blank reset.
        const draft = loadDraft();
        if (draft) {
          setSelectedEmployee(draft.selectedEmployee);
          setFormData(draft.formData);
        } else {
          setSelectedEmployee(null);
          setFormData({
            date_debut: new Date().toISOString().split('T')[0],
            date_fin: new Date().toISOString().split('T')[0],
            motif: '',
          });
        }
        setNbrJours(1);
      }
      setError(null);
    }
  }, [open, autorisation, employees, loadDraft]);

  // Calculate number of working days when dates change.
  // Excludes Sundays (weekly rest day) and public holidays (jours fériés)
  // to match the backend calculation.
  useEffect(() => {
    if (formData.date_debut && formData.date_fin) {
      const [sy, sm, sd] = formData.date_debut.split('-').map(Number);
      const [ey, em, ed] = formData.date_fin.split('-').map(Number);
      const start = new Date(sy, sm - 1, sd);
      const end = new Date(ey, em - 1, ed);
      if (end >= start) {
        let count = 0;
        const cur = new Date(start);
        while (cur <= end) {
          const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
          // getDay() === 0 -> Sunday
          if (cur.getDay() !== 0 && !feries.has(iso)) {
            count += 1;
          }
          cur.setDate(cur.getDate() + 1);
        }
        setNbrJours(count);
      } else {
        setNbrJours(0);
      }
    }
  }, [formData.date_debut, formData.date_fin, feries]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleEmployeeChange = (_: any, value: EmployeeDropdownItem | null) => {
    setSelectedEmployee(value);
    setError(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedEmployee) {
      setError('Veuillez selectionner un employe');
      return;
    }
    if (!formData.date_debut) {
      setError('La date de debut est requise');
      return;
    }
    if (!formData.date_fin) {
      setError('La date de fin est requise');
      return;
    }
    if (new Date(formData.date_fin) < new Date(formData.date_debut)) {
      setError('La date de fin doit etre superieure ou egale a la date de debut');
      return;
    }

    setLoading(true);
    try {
      if (autorisation) {
        const updateData: AutorisationAbsenceUpdate = {
          date_debut: formData.date_debut,
          date_fin: formData.date_fin,
          motif: formData.motif || null,
        };
        await autorisationsAbsenceAPI.update(autorisation.id, updateData);
        showSuccess('Autorisation d\'absence modifiee avec succes');
      } else {
        const createData: AutorisationAbsenceCreate = {
          employee_id: selectedEmployee.id,
          date_debut: formData.date_debut,
          date_fin: formData.date_fin,
          motif: formData.motif || null,
        };
        await autorisationsAbsenceAPI.create(createData);
        showSuccess('Autorisation d\'absence creee avec succes');
      }
      clearDraft();
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Annuler = deliberate cancel: discard any draft, then close.
  const handleClose = () => {
    clearDraft();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (!autorisation) {
        saveDraft({ selectedEmployee, formData });
      }
      onClose();
    }
  };

  return (
    <>
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={SlideUpTransition}
    >
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {autorisation ? 'Modifier l\'Autorisation d\'Absence' : 'Nouvelle Autorisation d\'Absence'}
          <DialogControls color="white" onMinimize={minimize} onClose={handleClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              options={employees}
              getOptionLabel={(option) => `${option.matricule} - ${option.nom_complet}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!!autorisation}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight="500">
                      {option.nom_complet}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.matricule} {option.fonction ? `- ${option.fonction}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employe *"
                  size="small"
                  placeholder="Rechercher par nom ou matricule..."
                />
              )}
              noOptionsText="Aucun employe trouve"
              clearText="Effacer"
              openText="Ouvrir"
              closeText="Fermer"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Date de Debut *"
              name="date_debut"
              type="date"
              value={formData.date_debut}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Date de Fin *"
              name="date_fin"
              type="date"
              value={formData.date_fin}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#E3F2FD', borderRadius: 1 }}>
              <Typography variant="body2">Nombre de jours:</Typography>
              <Chip
                label={`${nbrJours} jour(s)`}
                color={nbrJours > 0 ? 'primary' : 'error'}
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Motif / Raison"
              name="motif"
              multiline
              rows={3}
              value={formData.motif}
              onChange={handleChange}
              placeholder="Raison de l'absence (optionnel)..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || nbrJours <= 0}
          sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
        >
          {loading ? <CircularProgress size={24} /> : (autorisation ? 'Modifier' : 'Creer')}
        </Button>
      </DialogActions>
    </Dialog>

    <MinimizedBar
      open={open && minimized}
      color="#009688"
      title={autorisation ? 'Modifier l\'Autorisation d\'Absence' : 'Nouvelle Autorisation d\'Absence'}
      onRestore={restore}
      onClose={handleClose}
    />
    </>
  );
};

export default AutorisationAbsenceFormDialog;
